import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DollarSign, Coins, Wallet, Trophy, Percent, TrendingUp, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import DateRangePicker, { rangeToIsoParams } from '../components/mailer/DateRangePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mailer/dashboard`;

// "$24,500" / "$24.5K" / "$0.66" / "—" for unfilled spend.
function fmtMoney(n, compact = false) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (compact) {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000)     return `$${Math.round(v / 1_000)}k`;
  }
  // Sub-$10 values keep cents so cost-per-piece numbers like $0.62 don't
  // collapse to "$1" via rounding.
  if (Math.abs(v) > 0 && Math.abs(v) < 10) {
    return `$${v.toFixed(2)}`;
  }
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

function fmtCount(n) {
  if (n == null || isNaN(n)) return '0';
  return Number(n).toLocaleString('en-US');
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(1)}%`;
}

// Color the ROI cell: positive → emerald, slightly positive → amber, loss → red.
function roiClass(v) {
  if (v == null) return 'text-neutral-400';
  if (v >= 200)  return 'text-emerald-600 dark:text-emerald-400';
  if (v >= 0)    return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

export default function MailerSpend() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  // Date filter — shared `DateRangePicker` (split-panel UI). `rangeId`
  // identifies the preset (or "custom"); `customRange` carries the bespoke
  // {from, to} Dates the user pinned.
  const [rangeId, setRangeId] = useState('all');
  const [customRange, setCustomRange] = useState({ from: null, to: null });

  const range = useMemo(() => rangeToIsoParams({ rangeId, customRange }), [rangeId, customRange]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end)   params.end   = range.end;
    axios.get(`${API}/spend`, { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, [range.start, range.end]);

  const sources = useMemo(() => {
    const set = new Set(rows.map((r) => r.lead_source).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => (
    sourceFilter === 'all' ? rows : rows.filter((r) => r.lead_source === sourceFilter)
  ), [rows, sourceFilter]);

  // KPI strip — totals across the filtered set.
  const totals = useMemo(() => {
    const tracked = filtered.filter((r) => r.spend != null);
    const spend = tracked.reduce((s, r) => s + (r.spend || 0), 0);
    const leads = filtered.reduce((s, r) => s + (r.leads || 0), 0);
    const claimed = filtered.reduce((s, r) => s + (r.responses || 0), 0);
    const funded = filtered.reduce((s, r) => s + (r.funded || 0), 0);
    const revenue = filtered.reduce((s, r) => s + (r.revenue || 0), 0);
    const trackedClaimed = tracked.reduce((s, r) => s + (r.responses || 0), 0);
    const trackedLeads = tracked.reduce((s, r) => s + (r.leads || 0), 0);
    const trackedFunded = tracked.reduce((s, r) => s + (r.funded || 0), 0);
    // Avg Cost / Claimed: spend ÷ claimed leads — the true cost of acquiring
    // a broker-touched lead. Falls back to spend ÷ leads when no claims yet
    // so the tile never shows "$0" on a fresh upload.
    let cpl = null;
    if (trackedClaimed) cpl = spend / trackedClaimed;
    else if (trackedLeads) cpl = spend / trackedLeads;
    return {
      spend,
      leads,
      claimed,
      funded,
      revenue,
      net: revenue - spend,
      cpl,
      cpf: trackedFunded ? spend / trackedFunded : null,
      roi: spend ? ((revenue - spend) / spend) * 100 : null,
      untrackedCount: filtered.length - tracked.length,
    };
  }, [filtered]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 space-y-6" data-testid="mailer-spend-page">
      {/* Crumb */}
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">
        <span className="text-neutral-400">Admin</span>
        <span className="text-neutral-300">·</span>
        <span className="text-neutral-400">Mailer</span>
        <span className="text-neutral-300">·</span>
        <span className="text-[hsl(var(--kf-gold-deep))]">Spend</span>
      </div>

      {/* Title */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="display text-[28px] font-bold text-neutral-900 dark:text-white tracking-tight leading-none">
            Marketing Spend
          </h2>
          <p className="text-[13px] text-neutral-500 mt-2 max-w-[680px]">
            Spend captured at upload time across every mailer campaign. Cost-per-lead, cost-per-funded,
            and ROI are calculated automatically from your funded loans.
          </p>
        </div>
        {/* Filters: date range + source */}
        <div className="flex flex-col items-end gap-2">
          {/* Shared split-panel date picker (Month to Date, Last Month, etc.) */}
          <DateRangePicker
            value={{ rangeId, customRange }}
            onApply={(v) => { setRangeId(v.rangeId); setCustomRange(v.customRange); }}
            testIdPrefix="spend-daterange"
          />
          {/* Source filter chip strip */}
          <div className="inline-flex items-center gap-1 rounded-xl border hairline bg-white dark:bg-neutral-950 p-1" data-testid="spend-source-filter">
            {sources.map((s) => (
              <button
                key={s}
                data-testid={`spend-source-${s.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => setSourceFilter(s)}
                className={cn(
                  'h-8 px-3 rounded-lg text-[12px] font-semibold transition',
                  sourceFilter === s
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                    : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/[0.04]',
                )}
              >
                {s === 'all' ? 'All Sources' : s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI strip — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="spend-kpis">
        <KpiTile label="Total Spend"        value={fmtMoney(totals.spend)}                Icon={Wallet}     accent="neutral" />
        <KpiTile label="Rows Uploaded"      value={fmtCount(totals.leads)}                Icon={DollarSign} accent="sky" />
        <KpiTile label="Cost / Claimed"     value={fmtMoney(totals.cpl)}                  Icon={Coins}      accent="amber" />
        <KpiTile label="Funded Loans"       value={fmtCount(totals.funded)}               Icon={Trophy}     accent="emerald" />
        <KpiTile label="Net Revenue"        value={fmtMoney(totals.net)}                  Icon={TrendingUp} accent={totals.net >= 0 ? 'emerald' : 'rose'} />
        <KpiTile label="ROI"                value={fmtPct(totals.roi)}                    Icon={Percent}    accent={(totals.roi ?? 0) >= 200 ? 'emerald' : (totals.roi ?? 0) >= 0 ? 'amber' : 'rose'} />
      </div>

      {/* Untracked-spend banner */}
      {totals.untrackedCount > 0 && (
        <div className="rounded-xl border hairline bg-amber-50/60 dark:bg-amber-950/30 px-4 py-3 flex items-start gap-2.5" data-testid="spend-untracked-banner">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-[12.5px] text-neutral-700 dark:text-neutral-300 leading-snug">
            <span className="font-bold text-neutral-900 dark:text-white">{totals.untrackedCount} campaign{totals.untrackedCount === 1 ? '' : 's'}</span>
            {' '}don&rsquo;t have a marketing cost yet. ROI calculations below skip those rows — fill the cost during your next upload to get them counted.
          </div>
        </div>
      )}

      {/* Spend table */}
      <SpendTable rows={filtered} loading={loading} />
    </div>
  );
}

function KpiTile({ label, value, Icon, accent }) {
  const accents = {
    neutral: { ring: 'ring-neutral-200 dark:ring-white/[0.08]',   text: 'text-neutral-900 dark:text-white',         iconBg: 'bg-neutral-100 dark:bg-white/[0.06] text-neutral-700 dark:text-neutral-200' },
    sky:     { ring: 'ring-sky-200/70 dark:ring-sky-500/20',      text: 'text-sky-700 dark:text-sky-300',           iconBg: 'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-300' },
    amber:   { ring: 'ring-amber-200/70 dark:ring-amber-500/20',  text: 'text-amber-700 dark:text-amber-300',       iconBg: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-300' },
    emerald: { ring: 'ring-emerald-200/70 dark:ring-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', iconBg: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-300' },
    rose:    { ring: 'ring-rose-200/70 dark:ring-rose-500/20',    text: 'text-rose-700 dark:text-rose-300',         iconBg: 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-300' },
  };
  const a = accents[accent] || accents.neutral;
  return (
    <div data-testid={`spend-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={cn('rounded-xl bg-white dark:bg-neutral-950 ring-1 px-4 py-3.5', a.ring)}>
      <div className="flex items-center gap-2">
        <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center shrink-0', a.iconBg)}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400 truncate">{label}</div>
      </div>
      <div className={cn('display font-bold tabular-nums leading-none mt-2 text-[22px]', a.text)}>{value}</div>
    </div>
  );
}

function SpendTable({ rows, loading }) {
  // Grid: Drop | Campaign | Source | Rows Uploaded | Spend | CPL | Funded | CPF | Revenue | ROI
  const tpl = '100px minmax(180px,1.6fr) minmax(140px,1fr) 80px 110px 110px 80px 110px 110px 90px';
  return (
    <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden" data-testid="spend-table">
      <div
        className="grid gap-3 px-5 py-2.5 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 dark:text-neutral-400 border-b hairline bg-neutral-50/40 dark:bg-neutral-900/40"
        style={{ gridTemplateColumns: tpl }}
      >
        <div>Drop</div>
        <div>Campaign</div>
        <div>Source</div>
        <div className="text-right" title="CSV rows processed by the uploader for this campaign — includes new inserts AND refresh-updates of existing leads. May exceed live lead count when properties were re-mailed.">Rows Uploaded</div>
        <div className="text-right">Spend</div>
        <div className="text-right">Cost / Claimed</div>
        <div className="text-right">Funded</div>
        <div className="text-right">Cost / Funded</div>
        <div className="text-right">Revenue</div>
        <div className="text-right">ROI</div>
      </div>
      {loading ? (
        <div className="px-5 py-10 text-center text-[12.5px] text-neutral-500 dark:text-neutral-400 mono animate-pulse">Loading spend…</div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-[12.5px] text-neutral-500 dark:text-neutral-400">
          No campaigns yet. Upload a batch from <span className="font-semibold">Mailer → Upload</span> and enter the marketing cost.
        </div>
      ) : (
        <div className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
          {rows.map((r) => (
            <SpendRow key={`${r.campaign_name}__${r.lead_source}`} row={r} tpl={tpl} />
          ))}
        </div>
      )}
    </div>
  );
}

function SpendRow({ row, tpl }) {
  const dateLabel = row.drop_date
    ? new Date(row.drop_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
    : '—';
  const spendMissing = row.spend == null;
  return (
    <div
      data-testid={`spend-row-${row.campaign_name}`}
      className="grid gap-3 px-5 py-2.5 items-center text-[12.5px] hover:bg-neutral-50/60 dark:hover:bg-neutral-900/40 transition"
      style={{ gridTemplateColumns: tpl }}
    >
      <span className="text-neutral-500 dark:text-neutral-400 tabular-nums">{dateLabel}</span>
      <span className="font-semibold text-neutral-900 dark:text-white truncate" title={row.campaign_name}>{row.campaign_name}</span>
      <span className="text-neutral-700 dark:text-neutral-300 truncate" title={row.lead_source}>{row.lead_source}</span>
      <span className="text-right text-neutral-700 dark:text-neutral-300 tabular-nums">{fmtCount(row.leads)}</span>
      <span className={cn('text-right tabular-nums font-bold', spendMissing ? 'text-neutral-300 dark:text-neutral-600' : 'text-neutral-900 dark:text-white')}>
        {spendMissing ? '—' : fmtMoney(row.spend)}
      </span>
      <span className={cn('text-right tabular-nums', row.cost_per_lead == null ? 'text-neutral-300 dark:text-neutral-600' : 'text-amber-600 dark:text-amber-400 font-semibold')}>
        {row.cost_per_lead == null ? '—' : fmtMoney(row.cost_per_lead)}
      </span>
      <span className="text-right text-neutral-700 dark:text-neutral-300 tabular-nums">{fmtCount(row.funded)}</span>
      <span className={cn('text-right tabular-nums', row.cost_per_funded == null ? 'text-neutral-300 dark:text-neutral-600' : 'text-neutral-700 dark:text-neutral-300')}>
        {row.cost_per_funded == null ? '—' : fmtMoney(row.cost_per_funded)}
      </span>
      <span className="text-right text-emerald-600 dark:text-emerald-400 tabular-nums font-semibold">{fmtMoney(row.revenue)}</span>
      <span className={cn('text-right tabular-nums font-bold', roiClass(row.roi))}>
        {row.roi == null ? '—' : fmtPct(row.roi)}
      </span>
    </div>
  );
}
